import "@src/pages/businessAnalysis/businessAnalysis.css";
import "../compliance/compliance.css";
import "./appraisals.css";
import { useEffect, useMemo, useState } from "react";
import { Checkbox, DatePicker, Drawer, Dropdown, Input, InputNumber, Modal, Select, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getAppraisalQuery, getAppraisalRecordUrlQuery, getAppraisalReviewersQuery } from "@src/util/query/appraisal.query";
import { cancelAppraisalMutation, reassignAppraisalMutation, saveAppraisalReviewMutation, signOffAppraisalMutation, submitAppraisalSelfAssessmentMutation, takeAppraisalMutation } from "@src/util/mutation/appraisal.mutation";
import { answerText, describeRepeat, formatDate, formatScore, isAnswered, newId, openRecord, STATUS_META } from "./appraisalHelpers";
import { FormOnPdf } from "../formOnPdf/formOnPdf";

const forOfficer = (q) => q.answeredBy === "officer" || q.answeredBy === "both";
const forReviewer = (q) => q.answeredBy === "reviewer" || q.answeredBy === "both";
const answered = isAnswered;

const OBJECTIVE_STATUS = [
    { value: "open", label: "Open" },
    { value: "met", label: "Met" },
    { value: "notMet", label: "Not met" },
];

/** Read-only view of one side's answer. */
const AnswerText = ({ question, answer, scale }) => {
    if (!answer) return <span className="cmp-muted">Not answered</span>;
    return <span>{answerText(question, answer, scale) || "-"}</span>;
};

/** The input for one question: rating buttons, yes / no, a checkbox, a choice, a number, a date or text. */
const AnswerInput = ({ question, answer = {}, scale, onChange }) => (
    <>
        {question.type === "choice" && ((question.options || []).length <= 5 ? (
            <div className="apr-rating">
                {question.options.map((option) => (
                    <button key={option} type="button" className={`apr-rating-btn${answer.choice === option ? " selected" : ""}`} onClick={() => onChange({ choice: option })}>{option}</button>
                ))}
            </div>
        ) : (
            <Select className="custom-select" style={{ width: 320 }} showSearch placeholder="Choose" value={answer.choice || undefined} onChange={(choice) => onChange({ choice })}
                options={(question.options || []).map((o) => ({ value: o, label: o }))} />
        ))}
        {question.type === "checkbox" && <Checkbox checked={answer.answer === true} onChange={(e) => onChange({ answer: e.target.checked })}>{answer.answer === true ? "Checked" : "Check if it applies"}</Checkbox>}
        {question.type === "number" && <InputNumber className="generic-input-control" style={{ width: 200 }} value={answer.number ?? null} onChange={(v) => onChange({ number: v ?? undefined })} />}
        {question.type === "date" && <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={answer.date ? dayjs(answer.date) : null} onChange={(d) => onChange({ date: d ? d.format("YYYY-MM-DD") : undefined })} />}
        {question.type === "text" && <Input className="generic-input-control" maxLength={500} placeholder="Your answer" value={answer.comment || ""} onChange={(e) => onChange({ comment: e.target.value })} />}
        {question.type === "rating" && (
            <div className="apr-rating">
                {Array.from({ length: scale?.max || 5 }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" className={`apr-rating-btn${answer.rating === n ? " selected" : ""}`} onClick={() => onChange({ rating: n })}>
                        <span className="apr-rating-num">{n}</span>{scale?.labels?.[n - 1]}
                    </button>
                ))}
            </div>
        )}
        {question.type === "yesno" && (
            <div className="apr-rating">
                {[true, false].map((v) => (
                    <button key={String(v)} type="button" className={`apr-rating-btn${answer.answer === v ? " selected" : ""}`} onClick={() => onChange({ answer: v })}>{v ? "Yes" : "No"}</button>
                ))}
            </div>
        )}
        {["rating", "yesno", "comment"].includes(question.type) && (
            <Input.TextArea
                className="generic-input-control"
                autoSize={{ minRows: question.type === "comment" ? 3 : 1, maxRows: 8 }}
                placeholder={question.type === "comment" ? "Your answer" : "Comment (optional)"}
                value={answer.comment || ""}
                onChange={(e) => onChange({ comment: e.target.value })}
            />
        )}
    </>
);

/**
 * One appraisal: the reviewer fills it in and signs it here (with the officer's
 * self-assessment and a facts panel alongside); managers can also reassign or cancel it,
 * and anyone involved can download the signed record once it's complete.
 */
export const AppraisalDrawer = ({ visible, appraisalId, onClose, onChanged = () => {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const notify = (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));

    const { data, loading, refetch } = useQuery(getAppraisalQuery, { variables: { id: appraisalId }, skip: !visible || !appraisalId, fetchPolicy: "network-only" });
    const appraisal = data?.getAppraisal?.response;
    const viewer = appraisal?.viewer || {};
    const scale = appraisal?.template?.ratingScale;

    const [answers, setAnswers] = useState({});
    const [objectives, setObjectives] = useState([]);
    const [summary, setSummary] = useState("");
    const [dirty, setDirty] = useState(false);
    const [signOpen, setSignOpen] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignTo, setReassignTo] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [stopRepeating, setStopRepeating] = useState(false);
    // the appraisee's own side (office staff are appraised on the web; officers use the app)
    const [myAnswers, setMyAnswers] = useState({});
    const [response, setResponse] = useState("agree");
    const [myComment, setMyComment] = useState("");
    // on the company's own form: the form has to be read to the end before sending / signing
    const [readToEnd, setReadToEnd] = useState(false);

    const [saveReview, { loading: saving }] = useMutation(saveAppraisalReviewMutation);
    const [take, { loading: taking }] = useMutation(takeAppraisalMutation);
    const [reassign, { loading: reassigning }] = useMutation(reassignAppraisalMutation);
    const [cancel, { loading: cancelling }] = useMutation(cancelAppraisalMutation);
    const [submitSelf, { loading: submittingSelf }] = useMutation(submitAppraisalSelfAssessmentMutation);
    const [signOff, { loading: signingOff }] = useMutation(signOffAppraisalMutation);
    const [fetchRecord] = useLazyQuery(getAppraisalRecordUrlQuery);
    const { data: reviewersData } = useQuery(getAppraisalReviewersQuery, {
        variables: { employeeId: appraisal?.employeeId },
        skip: !reassignOpen || !appraisal?.employeeId,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (!appraisal) return;
        setAnswers(appraisal.reviewerAnswers || {});
        setObjectives((appraisal.objectives || []).map((o) => ({ ...o, id: o.id || newId() })));
        setSummary(appraisal.reviewerSummary || "");
        setMyAnswers(appraisal.officerAnswers || {});
        setDirty(false);
        setReadToEnd(false);
    }, [appraisal?._id, appraisal?.status, appraisal?.reviewerEmployeeId]);
    const onTheForm = appraisal?.template?.layout === "pdf" && !!appraisal?.formPdfUrl;
    const mustRead = onTheForm && !readToEnd;

    const questions = useMemo(() => (appraisal?.template?.sections || []).flatMap((s) => s.questions), [appraisal]);
    const unanswered = viewer.canReview ? questions.filter((q) => forReviewer(q) && q.required !== false && !answered(q, answers[q.id]))
        : viewer.canSelfAssess ? questions.filter((q) => forOfficer(q) && q.required !== false && !answered(q, myAnswers[q.id])) : [];
    // while the officer is still filling in their side they only see their own questions
    const reviewerVisible = !viewer.isOfficer || ["awaitingSignOff", "completed"].includes(appraisal?.status);

    const setAnswer = (id, patch) => {
        setDirty(true);
        setAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
    };
    const setObjective = (id, patch) => {
        setDirty(true);
        setObjectives((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
    };

    const submit = async (signOff) => {
        try {
            const res = await saveReview({
                variables: {
                    input: {
                        appraisalId: appraisal._id,
                        answers,
                        summary,
                        ...(appraisal.objectivesEnabled ? { objectives: objectives.map(({ id, text, dueDate, status, note, fromAppraisalId }) => ({ id, text, dueDate, status, note, fromAppraisalId })) } : {}),
                        ...(signOff ? { submit: true, typedName } : {}),
                    },
                },
            });
            notify(res?.data?.saveAppraisalReview?.message || "Saved");
            setDirty(false);
            setSignOpen(false);
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const sendSelfAssessment = async () => {
        try {
            await submitSelf({ variables: { input: { appraisalId: appraisal._id, answers: myAnswers } } });
            notify("Self-assessment sent to your reviewer");
            setDirty(false);
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const signMine = async () => {
        try {
            await signOff({ variables: { input: { appraisalId: appraisal._id, typedName, response, comment: myComment } } });
            notify("Appraisal signed - a copy is in your documents");
            setSignOpen(false);
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const takeIt = async () => {
        try {
            await take({ variables: { id: appraisal._id } });
            notify("It's yours - nobody else can pick it up now");
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
            refetch();
        }
    };

    const doReassign = async () => {
        try {
            const res = await reassign({ variables: { id: appraisal._id, reviewerEmployeeId: reassignTo } });
            notify(`Now with ${res?.data?.reassignAppraisal?.response?.reviewerName || "the new reviewer"}`);
            setReassignOpen(false);
            setReassignTo(null);
            await refetch();
            onChanged();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const doCancel = async () => {
        try {
            const res = await cancel({ variables: { id: appraisal._id, reason: cancelReason, stopRepeating } });
            notify(res?.data?.cancelAppraisal?.message || "Appraisal cancelled");
            setCancelOpen(false);
            setCancelReason("");
            setStopRepeating(false);
            onChanged();
            onClose();
        } catch (err) {
            notify(err.message, true);
        }
    };

    const close = () => {
        if (!dirty) return onClose();
        Modal.confirm({
            title: "Leave without saving?",
            content: "Your changes to this appraisal haven't been saved.",
            okText: "Leave",
            okButtonProps: { danger: true },
            cancelText: "Keep editing",
            onOk: onClose,
        });
    };

    const status = STATUS_META[appraisal?.status] || {};
    const manageItems = [
        viewer.canReassign && { key: "reassign", label: "Change reviewer" },
        viewer.canCancel && { key: "cancel", label: "Cancel appraisal", danger: true },
    ].filter(Boolean);

    const renderReviewerInput = (question) => {
        if (viewer.canReview) return <AnswerInput question={question} answer={answers[question.id]} scale={scale} onChange={(patch) => setAnswer(question.id, patch)} />;
        return appraisal.reviewerSubmittedAt || Object.keys(appraisal.reviewerAnswers || {}).length
            ? <div><b className="cmp-muted" style={{ marginRight: 6 }}>Reviewer</b><AnswerText question={question} answer={appraisal.reviewerAnswers?.[question.id]} scale={scale} /></div>
            : null;
    };

    return (
        <Drawer
            title={appraisal ? `${appraisal.employeeName} - ${appraisal.title}` : "Appraisal"}
            placement="right"
            open={visible}
            onClose={saving ? undefined : close}
            width={onTheForm ? 1040 : 860}
            rootClassName="apr-form"
            maskClosable={false}
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon"  />}
            footer={appraisal && (
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {viewer.canReview ? (
                        <>
                            <button className={saving || unanswered.length || mustRead ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={saving || unanswered.length > 0 || mustRead} onClick={() => setSignOpen(true)}>
                                Sign and send to officer
                            </button>
                            <button className={saving ? "disabled-btn drawer-outlined-btn" : "drawer-outlined-btn"} disabled={saving} onClick={() => submit(false)}>
                                {saving ? <Spinner /> : "Save draft"}
                            </button>
                            {unanswered.length > 0 ? <span className="cmp-hint">{unanswered.length} required {unanswered.length === 1 ? "question" : "questions"} to answer</span>
                                : mustRead ? <span className="cmp-hint">Scroll to the end of the form to sign</span> : null}
                        </>
                    ) : viewer.canSelfAssess ? (
                        <>
                            <button className={submittingSelf || unanswered.length || mustRead ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={submittingSelf || unanswered.length > 0 || mustRead} onClick={sendSelfAssessment}>
                                {submittingSelf ? <Spinner /> : "Send to my reviewer"}
                            </button>
                            {unanswered.length > 0 ? <span className="cmp-hint">{unanswered.length} required {unanswered.length === 1 ? "question" : "questions"} to answer</span>
                                : mustRead ? <span className="cmp-hint">Scroll to the end of the form to send</span> : null}
                        </>
                    ) : viewer.canSign ? (
                        <>
                            <button className={mustRead ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={mustRead} onClick={() => setSignOpen(true)}>Read and sign</button>
                            {mustRead && <span className="cmp-hint">Read the form to the end to sign</span>}
                        </>
                    ) : appraisal.status === "completed" ? (
                        <button className="drawer-filled-btn" onClick={() => openRecord(fetchRecord, appraisal._id, notify)}>Download signed record</button>
                    ) : (
                        <button className="drawer-outlined-btn" onClick={onClose}>Close</button>
                    )}
                </div>
            )}
        >
            {loading || !appraisal ? (
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner /></div>
            ) : (
                <>
                    <div className="apr-meta">
                        <span><Tag color={status.color}>{status.label}</Tag>{appraisal.overdue && <Tag color="red">Overdue</Tag>}</span>
                        <span>Due <b className={appraisal.overdue ? "cmp-overdue" : ""}>{formatDate(appraisal.dueDate)}</b></span>
                        <span>Reviewer <b>{appraisal.reviewerName || (appraisal.candidateCount ? `shared between ${appraisal.candidateCount}` : "not assigned")}</b></span>
                        {appraisal.status === "completed" && <span>Score <b>{formatScore(appraisal)}</b></span>}
                        {appraisal.employeeNo && <span>Employee no. <b>{appraisal.employeeNo}</b></span>}
                        {appraisal.schedule && (
                            <span>↻ <b>{describeRepeat(appraisal.schedule.everyMonths)}</b>{appraisal.schedule.active ? ` - next opens ${formatDate(appraisal.schedule.nextOpenOn)}` : " (stopped)"}</span>
                        )}
                        <button className="cmp-link" onClick={() => navigate(`/user/employee-detail/${appraisal.employeeId}/tab/profile`)}>View profile</button>
                        {appraisal.formPdfUrl && <a className="cmp-link" href={appraisal.formPdfUrl} target="_blank" rel="noreferrer">See the form</a>}
                        {manageItems.length > 0 && (
                            <span style={{ marginLeft: "auto" }}>
                                <Dropdown trigger={["click"]} menu={{ items: manageItems, onClick: ({ key }) => (key === "reassign" ? setReassignOpen(true) : setCancelOpen(true)) }}>
                                    <button className="sm-btn">More ▾</button>
                                </Dropdown>
                            </span>
                        )}
                    </div>

                    {appraisal.status === "cancelled" && (
                        <div className="ba-callout warning"><div><div className="ba-callout-title">Cancelled</div><div className="ba-callout-text">{appraisal.cancelledReason || "No reason given."}</div></div></div>
                    )}

                    {viewer.canTake && (
                        <div className="ba-callout info" style={{ marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                                <div className="ba-callout-title">Waiting for a reviewer</div>
                                <div className="ba-callout-text">This appraisal is shared between {appraisal.candidateCount} people with the reviewer role. Take it to fill it in - it then leaves everyone else's list.</div>
                            </div>
                            <button className="drawer-filled-btn" disabled={taking} onClick={takeIt}>{taking ? <Spinner /> : "Take this appraisal"}</button>
                        </div>
                    )}

                    {viewer.canSelfAssess && (
                        <div className="ba-callout info" style={{ marginBottom: 14 }}>
                            <div><div className="ba-callout-title">Your self-assessment</div>
                                <div className="ba-callout-text">Answer these questions about your own work. Your reviewer sees your answers once you send them, then completes the appraisal.</div></div>
                        </div>
                    )}

                    {appraisal.status === "awaitingOfficer" && !viewer.isOfficer && (
                        <div className="ba-callout info" style={{ marginBottom: 14 }}>
                            <div><div className="ba-callout-title">Waiting for the officer's self-assessment</div>
                                <div className="ba-callout-text">{appraisal.employeeName} answers their questions in the app first. You'll be notified when it's ready for you.</div></div>
                        </div>
                    )}

                    {appraisal.facts && (
                        <div className="apr-facts">
                            <div><div className="apr-fact-label">Joined</div><div className="apr-fact-value">{appraisal.facts.joined ? formatDate(appraisal.facts.joined) : "-"}</div></div>
                            {appraisal.facts.position && <div><div className="apr-fact-label">Position</div><div className="apr-fact-value">{appraisal.facts.position}</div></div>}
                            <div>
                                <div className="apr-fact-label">Documents to sign</div>
                                <div className={`apr-fact-value${appraisal.facts.complianceOverdue ? " cmp-overdue" : ""}`}>
                                    {appraisal.facts.complianceOutstanding}{appraisal.facts.complianceOverdue ? ` (${appraisal.facts.complianceOverdue} overdue)` : ""}
                                </div>
                            </div>
                            <div>
                                <div className="apr-fact-label">Previous appraisals</div>
                                <div className="apr-fact-value">
                                    {appraisal.facts.previousAppraisals.length === 0 ? "None" : appraisal.facts.previousAppraisals.map((p) => (
                                        <div key={p._id} style={{ fontWeight: 500, fontSize: 13 }}>{p.title} - {formatDate(p.completedAt)}{p.overallRating != null ? ` - ${p.overallRating}/${p.ratingMax || 5}` : ""}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {(appraisal.status === "awaitingSignOff" || appraisal.status === "completed") && (
                        <div className={`apr-signed${appraisal.officerResponse === "disagree" ? " disagree" : ""}`}>
                            {appraisal.status === "awaitingSignOff"
                                ? viewer.isOfficer
                                    ? <><b>{appraisal.reviewerName}</b> completed and signed your appraisal on {formatDate(appraisal.reviewerSubmittedAt)}. Read it below, then sign it.</>
                                    : <>Signed by <b>{appraisal.reviewerName}</b> on {formatDate(appraisal.reviewerSubmittedAt)} - waiting for {appraisal.employeeName} to read and sign it.</>
                                : <>
                                    <b>{appraisal.employeeName}</b> signed on {formatDate(appraisal.officerSignedAt)} and {appraisal.officerResponse === "disagree" ? <b>does not agree</b> : "agrees"} with it.
                                    {appraisal.officerComment && <div style={{ marginTop: 4 }}>"{appraisal.officerComment}"</div>}
                                </>}
                        </div>
                    )}

                    {onTheForm && (() => {
                        const officerShown = appraisal.selfAssessment && (appraisal.officerSubmittedAt || viewer.isOfficer) ? appraisal.officerAnswers || {} : {};
                        const filling = viewer.canReview ? "reviewer" : viewer.canSelfAssess ? "officer" : null;
                        return (
                            <div className="apr-section" style={{ padding: 10, background: "#f4f7f9" }}>
                                {filling && <div className="cmp-hint" style={{ margin: "0 0 10px 4px" }}>Start takes you through each highlighted box in turn, or click any box to fill it in. Read the form to the end to {filling === "reviewer" ? "sign it" : "send it"}.</div>}
                                <FormOnPdf pdfUrl={appraisal.formPdfUrl} template={appraisal.template} kind="appraisal" mode={filling ? "fill" : "view"} side={filling}
                                    answers={filling === "reviewer" ? answers : myAnswers}
                                    onAnswer={filling === "reviewer" ? setAnswer : (id, patch) => { setDirty(true); setMyAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } })); }}
                                    shown={{ officer: officerShown, reviewer: reviewerVisible && filling !== "reviewer" ? appraisal.reviewerAnswers || {} : {} }}
                                    stampValues={appraisal.stampValues || {}} onReadToEnd={() => setReadToEnd(true)}
                                    renderBar={({ answered, total, left, next, nextLabel }) => (
                                        <div className="fop-bar" style={{ background: "#f4f7f9" }}>
                                            <span className="fop-bar-count">{answered} of {total} answered</span>
                                            {left > 0 && <button type="button" className="svy-clear" data-next-field onClick={next}>{nextLabel}</button>}
                                            {mustRead && <span className="fop-bar-count" style={{ marginLeft: "auto" }}>Read to the end of the form</span>}
                                        </div>
                                    )} />
                            </div>
                        );
                    })()}
                    {!onTheForm && appraisal.template.sections.map((section) => (
                        <div className="apr-section" key={section.id}>
                            <div className="apr-section-title">{section.title}</div>
                            {section.questions.map((question) => {
                                const officerAnswer = appraisal.officerAnswers?.[question.id];
                                const showOfficer = appraisal.selfAssessment && forOfficer(question) && (appraisal.officerSubmittedAt || viewer.isOfficer);
                                const showReviewer = forReviewer(question) && reviewerVisible;
                                if (!showReviewer && !showOfficer) return null;
                                const required = question.required !== false && ((viewer.canReview && forReviewer(question)) || (viewer.canSelfAssess && forOfficer(question)));
                                return (
                                    <div className="apr-question" key={question.id}>
                                        <div className="apr-question-text">
                                            {question.text}{required && <span className="apr-required">*</span>}
                                        </div>
                                        {showOfficer && (viewer.canSelfAssess
                                            ? <AnswerInput question={question} answer={myAnswers[question.id]} scale={scale} onChange={(patch) => { setDirty(true); setMyAnswers((prev) => ({ ...prev, [question.id]: { ...(prev[question.id] || {}), ...patch } })); }} />
                                            : <div className="apr-officer-answer"><b>{viewer.isOfficer ? "You" : "Officer"}</b><AnswerText question={question} answer={officerAnswer} scale={scale} /></div>
                                        )}
                                        {showReviewer && renderReviewerInput(question)}
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {appraisal.objectivesEnabled && (viewer.canReview || objectives.length > 0) && (
                        <div className="apr-section">
                            <div className="apr-section-title">Objectives</div>
                            {objectives.length === 0 && <div className="cmp-empty">No objectives yet.</div>}
                            {objectives.map((objective) => viewer.canReview ? (
                                <div key={objective.id}>
                                    <div className="apr-objective">
                                        <Input className="generic-input-control" value={objective.text} placeholder="e.g. Complete first aid refresher" onChange={(e) => setObjective(objective.id, { text: e.target.value })} />
                                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={objective.dueDate ? dayjs(objective.dueDate) : null} placeholder="By (optional)"
                                            onChange={(d) => setObjective(objective.id, { dueDate: d ? d.format("YYYY-MM-DD") : "" })} />
                                        <Select className="custom-select" value={objective.status || "open"} options={OBJECTIVE_STATUS} onChange={(v) => setObjective(objective.id, { status: v })} />
                                        <button className="apr-icon-btn" title="Remove" onClick={() => { setDirty(true); setObjectives((prev) => prev.filter((o) => o.id !== objective.id)); }}><FontAwesomeIcon icon={faTrash} /></button>
                                    </div>
                                    {objective.fromAppraisalId && <div className="cmp-hint" style={{ marginTop: -4, marginBottom: 8 }}>Carried over from the last appraisal - mark it met or not met, or leave it open.</div>}
                                </div>
                            ) : (
                                <div key={objective.id} style={{ marginBottom: 6 }}>
                                    {objective.text}{objective.dueDate ? <span className="cmp-muted"> - by {formatDate(objective.dueDate)}</span> : null}
                                    {objective.status && objective.status !== "open" && <Tag style={{ marginLeft: 8 }} color={objective.status === "met" ? "green" : "red"}>{objective.status === "met" ? "Met" : "Not met"}</Tag>}
                                </div>
                            ))}
                            {viewer.canReview && (
                                <button className="cmp-link" onClick={() => { setDirty(true); setObjectives((prev) => [...prev, { id: newId(), text: "", dueDate: "", status: "open" }]); }}>
                                    <FontAwesomeIcon icon={faPlus} /> Add objective
                                </button>
                            )}
                        </div>
                    )}

                    {(viewer.canReview || appraisal.reviewerSummary) && (
                        <div className="apr-section">
                            <div className="apr-section-title">Reviewer's summary</div>
                            {viewer.canReview ? (
                                <Input.TextArea className="generic-input-control" autoSize={{ minRows: 3, maxRows: 10 }} value={summary} placeholder="Overall summary for the officer"
                                    onChange={(e) => { setDirty(true); setSummary(e.target.value); }} />
                            ) : <div style={{ whiteSpace: "pre-wrap" }}>{appraisal.reviewerSummary}</div>}
                        </div>
                    )}
                </>
            )}

            <Modal
                open={signOpen}
                width={520}
                closable={false}
                onCancel={() => setSignOpen(false)}
                footer={
                    <div className="drawer-footer" style={{ display: "flex", gap: 8 }}>
                        <button className={saving || signingOff || typedName.trim().length < 2 ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={saving || signingOff || typedName.trim().length < 2} onClick={() => (viewer.canSign ? signMine() : submit(true))}>
                            {saving || signingOff ? <Spinner /> : viewer.canSign ? "Sign" : "Sign and send"}
                        </button>
                        <button className="drawer-outlined-btn" disabled={saving || signingOff} onClick={() => setSignOpen(false)}>Cancel</button>
                    </div>
                }
            >
                <div className="modal-header-title">
                    <span style={{ letterSpacing: "0.2px" }}>Sign the appraisal</span>
                    <span onClick={() => setSignOpen(false)}><FontAwesomeIcon className="close" icon={faClose} /></span>
                </div>
                <div className="modal-body apr-form" style={{ margin: "16px 0 0" }}>
                    {viewer.canSign ? (
                        <>
                            <div className="cmp-field">
                                <label>Do you agree with this appraisal?</label>
                                <div className="cmp-choice">
                                    <button type="button" className={`cmp-choice-card${response === "agree" ? " selected" : ""}`} onClick={() => setResponse("agree")}>
                                        <div className="cmp-choice-card-title">I agree</div>
                                    </button>
                                    <button type="button" className={`cmp-choice-card${response === "disagree" ? " selected" : ""}`} onClick={() => setResponse("disagree")}>
                                        <div className="cmp-choice-card-title">I don't agree</div>
                                    </button>
                                </div>
                            </div>
                            <div className="cmp-field">
                                <label>Your comments <span className="cmp-muted">({response === "disagree" ? "say why" : "optional"})</span></label>
                                <Input.TextArea className="generic-input-control" rows={3} value={myComment} onChange={(e) => setMyComment(e.target.value)} />
                            </div>
                            <div className="cmp-hint" style={{ marginBottom: 12 }}>Signing confirms you've read it - you can sign even if you don't agree. Your comments are kept with it.</div>
                        </>
                    ) : (
                        <div className="cmp-hint" style={{ marginBottom: 12 }}>
                            Once signed it can't be changed. {appraisal?.employeeName} is asked to read it, say whether they agree, and sign it too.
                        </div>
                    )}
                    <div className="cmp-field">
                        <label>Type your full name to sign</label>
                        <Input className="generic-input-control" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Your full name" autoFocus />
                    </div>
                </div>
            </Modal>

            <Modal
                open={reassignOpen}
                width={520}
                closable={false}
                onCancel={() => setReassignOpen(false)}
                footer={
                    <div className="drawer-footer" style={{ display: "flex", gap: 8 }}>
                        <button className={reassigning || !reassignTo ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={reassigning || !reassignTo} onClick={doReassign}>
                            {reassigning ? <Spinner /> : "Change reviewer"}
                        </button>
                        <button className="drawer-outlined-btn" disabled={reassigning} onClick={() => setReassignOpen(false)}>Cancel</button>
                    </div>
                }
            >
                <div className="modal-header-title">
                    <span style={{ letterSpacing: "0.2px" }}>Change reviewer</span>
                    <span onClick={() => setReassignOpen(false)}><FontAwesomeIcon className="close" icon={faClose} /></span>
                </div>
                <div className="modal-body apr-form" style={{ margin: "16px 0 0" }}>
                    <div className="cmp-field">
                        <label>New reviewer</label>
                        <Select className="custom-select" showSearch optionFilterProp="label" value={reassignTo} onChange={setReassignTo} placeholder="Choose someone with a WFC360 login"
                            options={(reviewersData?.getAppraisalReviewers?.response || []).filter((r) => r._id !== appraisal?.reviewerEmployeeId).map((r) => ({ value: r._id, label: r.name }))} />
                        <span className="cmp-hint">Anything the current reviewer has saved stays on the appraisal.</span>
                    </div>
                </div>
            </Modal>

            <Modal
                open={cancelOpen}
                width={520}
                closable={false}
                onCancel={() => setCancelOpen(false)}
                footer={
                    <div className="drawer-footer" style={{ display: "flex", gap: 8 }}>
                        <button className={cancelling ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} style={{ background: "#b42318", borderColor: "#b42318" }} disabled={cancelling} onClick={doCancel}>
                            {cancelling ? <Spinner /> : "Cancel appraisal"}
                        </button>
                        <button className="drawer-outlined-btn" disabled={cancelling} onClick={() => setCancelOpen(false)}>Keep it</button>
                    </div>
                }
            >
                <div className="modal-header-title">
                    <span style={{ letterSpacing: "0.2px" }}>Cancel this appraisal?</span>
                    <span onClick={() => setCancelOpen(false)}><FontAwesomeIcon className="close" icon={faClose} /></span>
                </div>
                <div className="modal-body apr-form" style={{ margin: "16px 0 0" }}>
                    <div className="cmp-field">
                        <label>Reason <span className="cmp-muted">(optional)</span></label>
                        <Input.TextArea className="generic-input-control" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Officer has left" />
                    </div>
                    {appraisal?.schedule?.active && (
                        <div className="cmp-field">
                            <Checkbox checked={stopRepeating} onChange={(e) => setStopRepeating(e.target.checked)}>
                                Also stop repeating - {describeRepeat(appraisal.schedule.everyMonths).toLowerCase()}, next on {formatDate(appraisal.schedule.nextOpenOn)}
                            </Checkbox>
                        </div>
                    )}
                </div>
            </Modal>
        </Drawer>
    );
};
